import { useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { CreateTemplateValidation, useCreateAchTemplate } from "@/lib/api/ach";
import { safeParseDecimal } from "@/lib/api/common";
import { PdfField } from "../types";
import { UploadPresignedFile } from "@/lib/file";

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: PdfField[];
  originalFile: File;
  onSuccess: () => void;
}

interface TemplateFormData {
  ach_variant: "with_lien_holder" | "without_lien_holder";
  version_label: string;
  version_reason: string;
  name: string;
}

function generateDefaultFormData(): TemplateFormData {
  return {
    ach_variant: "without_lien_holder",
    version_label: "v1",
    version_reason: "v1",
    name: "ACH Authorization",
  }
}

export function CreateTemplateDialog({ open, onOpenChange, fields, originalFile, onSuccess }: CreateTemplateDialogProps) {
  const { toast } = useToast();
  const createAchTemplate = useCreateAchTemplate();
  const [formData, setFormData] = useState<TemplateFormData>(generateDefaultFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formData.ach_variant || !formData.version_label) {
      toast({
        title: "Error",
        description: "ACH variant and version label are required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const processedFields = fields.map(field => {
        const isLenderField = /lender/i.test(field.field_id || '');
        const isRequiredAttachment = /attachment_voided_check/i.test(field.field_id || '');
        return {
          field_type: field.field_type,
          field_id: field.field_id,
          field_key: field.field_key,
          system_field_type: field.system_field_type,
          field_dest_type: field.field_dest_type || genFieldDestType(field.field_type, field.field_id),
          position_data: {
            x: safeParseDecimal({})(field.position_data.x).toString(),
            y: safeParseDecimal({})(field.position_data.y).toString(),
            width: safeParseDecimal({})(field.position_data.width).toString(),
            height: safeParseDecimal({})(field.position_data.height).toString(),
            text_size: safeParseDecimal({})(field.position_data.text_size).toNumber(),
            page: safeParseDecimal({})(field.position_data.page).toNumber(),
          },
          freeform_value: field.freeform_value,
          signer_id: isLenderField ? "2" : "1",
          is_required: (field.is_required ?? false) || isRequiredAttachment,
          is_readonly: field.is_readonly ?? false,
        };
      });

      const parseResult = CreateTemplateValidation.safeParse({
        ach_variant: formData.ach_variant,
        template_content_type: "ach",
        parent_group_id: uuidV4(),
        template: {
          name: formData.name ? formData.name : undefined,
          version_label: formData.version_label,
          version_reason: formData.version_reason,
          require_all_signers: true,
          send_auto_reminders: false,
          use_signer_order: true,
          fields: processedFields,
        }
      });

      if (!parseResult.success) {
        console.error("failed to parse api body", parseResult.error);
        toast({
          title: "Error",
          description: "Invalid data",
          variant: "destructive",
        });
        return;
      }

      const apiResult = await createAchTemplate(parseResult.data)
      if (!apiResult.success) {
        throw apiResult;
      }

      const uploadResult = await UploadPresignedFile(apiResult.data.upload_url, originalFile)
      if (!uploadResult.success) {
        toast({
          title: "Error",
          description: "Failed to upload file",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Created Template",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create template",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create ACH Template</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="ACH Authorization"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="version_label">Version Label</Label>
              <Input
                id="version_label"
                value={formData.version_label}
                onChange={(e) => setFormData(prev => ({ ...prev, version_label: e.target.value }))}
                placeholder="v1"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="version_reason">Version Reason (Optional)</Label>
              <Textarea
                id="version_reason"
                value={formData.version_reason}
                onChange={(e) => setFormData(prev => ({ ...prev, version_reason: e.target.value }))}
                placeholder="Enter reason for this version..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function genFieldDestType(field_type: string, field_id: string) {
  if (field_type !== 'signature') {
    return undefined;
  }

  if (/date/i.test(field_id || '')) {
    return 'date';
  }

  if (/attachment/i.test(field_id || '')) {
    return 'attachment';
  }

  return 'signature';
}




